import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerModal } from '../Modal'
import PerText from '../Text'
import PerFlexRow from '../FlexRow'
import { KudosDto } from '../../../types'
import { deleteKudos } from '../../../graphql/kudos/useKudosMutations'
import { KudosFilterInput } from '../../../types'
import { useAuth } from '../../../context/auth-context'
import { getKudosColor } from '../../../shared/kudosUtil'
import { useDateTime } from '../../../shared/useDateTime'
import { showNotification } from '../Notification/notification'
import { PerAvatarPlus } from '../AvatarPlus'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'
import PerIcon from '../Icon'

export interface DeleteKudosModalProps {
  visible: boolean
  onClose: () => void
  kudos: KudosDto | null
  filters: KudosFilterInput
  replyerView: boolean
}

export const DeleteKudosModal: FC<DeleteKudosModalProps> = ({ visible, onClose, kudos, filters, replyerView }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatDateShort } = useDateTime(user)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const handleDelete = async () => {
    if (!kudos) return

    setIsLoading(true)
    setErrorMessage('')

    const result = await deleteKudos(kudos.id, filters, replyerView)

    if (!!result.data && !result.errors) {
      resetAndClose()
      showNotification({
        type: 'success',
        message: t('kudos.notifications.delete.successTitle'),
        description: t('kudos.notifications.delete.successDescription'),
      })
    } else {
      const errorMessage = result.errors?.[0]?.extensions?.validationErrors?.[0]?.message || t('kudos.delete.error.generic')
      setErrorMessage(errorMessage)
      setIsLoading(false)
    }
  }

  const resetAndClose = () => {
    setErrorMessage('')
    setIsLoading(false)
    onClose()
  }

  if (!kudos) return null

  return (
    <PerModal
      title={{ heading: t('kudos.delete.confirmTitle') }}
      size="medium"
      centered
      visible={visible}
      onCancel={resetAndClose}
      footer={{
        cancelBtn: {
          onClick: resetAndClose,
        },
        confirmBtn: {
          onClick: handleDelete,
          disabled: isLoading || !!errorMessage,
          isProcessing: { active: isLoading },
        },
      }}
    >
      <PerFlexRow direction="column">
        {kudos.author && kudos.employee && (
          <PerFlexRow align="bottom">
            {/* From Section */}
            <div style={{ maxWidth: '44%' }}>
              <PerFlexRow direction="column" justify="start" mr="xxl">
                <PerText fontSize="s" color="secondary" mb="xxxs">
                  {t('global.from')}
                </PerText>
                {kudos.authorId === user.employeeId && <div style={{ marginBottom: '2.5px' }}></div>}
                <PerAvatarPlus
                  firstName={kudos.author.firstName}
                  lastName={kudos.author.lastName}
                  nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
                  infoText={kudos.author.companyName}
                  nameType={kudos.authorId === user.employeeId ? 'you' : 'fullName'}
                />
                {kudos.authorId === user.employeeId && <div style={{ marginBottom: '2.5px' }}></div>}
              </PerFlexRow>
            </div>

            {/* Arrow Section */}
            <div style={{ width: '12%', marginBottom: '2px' }}>
              <PerIcon name="arrowRight_stroke" size={18} color={getKudosColor(kudos.type)} />
            </div>

            {/* To Section */}
            <div style={{ maxWidth: '44%' }}>
              <PerFlexRow direction="column" justify="start">
                <PerText fontSize="s" color="secondary" mb="xxxs">
                  {t('global.to')}
                </PerText>
                {kudos.employeeId === user.employeeId && <div style={{ marginBottom: '2.5px' }}></div>}
                <PerAvatarPlus
                  firstName={kudos.employee.firstName}
                  lastName={kudos.employee.lastName}
                  nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
                  infoText={kudos.employee.companyName}
                  nameType={kudos.employeeId === user.employeeId ? 'you' : 'fullName'}
                />
                {kudos.employeeId === user.employeeId && <div style={{ marginBottom: '2.5px' }}></div>}
              </PerFlexRow>
            </div>
          </PerFlexRow>
        )}

        <PerText fontSize="s" color="secondary" mt="l" mb="xxs">
          {formatDateShort(kudos.createdDate)}
        </PerText>
        <PerText mb="xxxl">"{kudos.description}"</PerText>

        {/* Confirmation Message */}
        {errorMessage ? (
          <PerText color="error" fontSize="m">
            {errorMessage}
          </PerText>
        ) : (
          <PerText fontSize="m" fontWeight="bold" color="error">
            {t('kudos.delete.confirmMessage')}
          </PerText>
        )}
      </PerFlexRow>
    </PerModal>
  )
}

export default DeleteKudosModal
